import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO } from 'date-fns';

const { FiX, FiCalendar, FiClock, FiUser, FiMapPin, FiCheck, FiInfo, FiAlertTriangle } = FiIcons;

const VisitModal = ({ isOpen, onClose, onSave, properties, initialData }) => {
  const [formData, setFormData] = useState({
    propertyId: '',
    visitorName: '',
    visitorContact: '',
    visitDate: format(new Date(), 'yyyy-MM-dd'),
    startTime: '10:00',
    duration: '30',
    type: 'Showing',
    notes: '',
    status: 'Scheduled',
    notifyTenant: true
  });

  const visitTypes = ['Showing', 'Inspection', 'Appraisal', 'Maintenance Walkthrough', 'Owner Visit'];

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({
        propertyId: '',
        visitorName: '',
        visitorContact: '',
        visitDate: format(new Date(), 'yyyy-MM-dd'),
        startTime: '10:00',
        duration: '30',
        type: 'Showing',
        notes: '',
        status: 'Scheduled',
        notifyTenant: true
      });
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const selectedProperty = properties.find(p => p.id === formData.propertyId);
  const isOccupied = selectedProperty?.status === 'Occupied';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-200">
        <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl">
              <SafeIcon icon={FiCalendar} />
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900">{initialData ? 'Edit Appointment' : 'Schedule Visit'}</h2>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Property Access Management</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <SafeIcon icon={FiX} size={24} />
          </button>
        </div>

        <form className="p-8 space-y-6 max-h-[75vh] overflow-y-auto">
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Target Property</label>
              <select 
                required 
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                value={formData.propertyId}
                onChange={e => setFormData({ ...formData, propertyId: e.target.value })}
              >
                <option value="">Select Property...</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.status})</option>
                ))}
              </select>
            </div>

            {isOccupied && (
              <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 flex gap-3 animate-in fade-in slide-in-from-top-2">
                <SafeIcon icon={FiAlertTriangle} className="text-amber-500 shrink-0" />
                <div>
                  <p className="text-[10px] text-amber-700 font-black uppercase">Occupied Unit Alert</p>
                  <p className="text-[10px] text-amber-600 font-medium">
                    This unit is currently occupied by {selectedProperty.tenantName}. A 24-hour notice is recommended.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Visitor Name</label>
                <div className="relative">
                  <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                  <input 
                    required 
                    className="w-full pl-9 p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none"
                    value={formData.visitorName}
                    onChange={e => setFormData({ ...formData, visitorName: e.target.value })}
                    placeholder="e.g. John Doe"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Visit Type</label>
                <select 
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none"
                  value={formData.type}
                  onChange={e => setFormData({ ...formData, type: e.target.value })}
                >
                  {visitTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-1">
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Date</label>
                <input 
                  type="date" 
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs font-bold outline-none"
                  value={formData.visitDate}
                  onChange={e => setFormData({ ...formData, visitDate: e.target.value })}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Start Time</label>
                <input 
                  type="time" 
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs font-bold outline-none"
                  value={formData.startTime}
                  onChange={e => setFormData({ ...formData, startTime: e.target.value })}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Duration (min)</label>
                <input 
                  type="number" 
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs font-bold outline-none"
                  value={formData.duration}
                  onChange={e => setFormData({ ...formData, duration: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Internal Notes</label>
              <textarea 
                rows="2" 
                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500"
                value={formData.notes}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Entry instructions, gate codes, or specific showing focus..."
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-2xl">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiInfo} className="text-indigo-600" />
                <div>
                  <p className="text-xs font-black text-indigo-900 uppercase">Tenant Notification</p>
                  <p className="text-[10px] text-indigo-600 font-bold">Auto-send visit notice to resident</p>
                </div>
              </div>
              <button 
                type="button"
                onClick={() => setFormData({...formData, notifyTenant: !formData.notifyTenant})}
                className={`w-12 h-6 rounded-full transition-colors relative ${formData.notifyTenant ? 'bg-indigo-600' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.notifyTenant ? 'left-7' : 'left-1'}`} />
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 font-bold text-gray-400 hover:text-gray-600 transition">Cancel</button>
            <button 
              type="button" 
              onClick={() => { onSave(formData); onClose(); }} 
              className="flex-1 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiCheck} /> {initialData ? 'Update Visit' : 'Confirm Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VisitModal;